import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  CoPresentOutlined as DemoIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import { useSnackbar } from './CentralSnackbar';
import AddDemoPatientDialog from './AddDemoPatientDialog';
import { formatDateSync, waitForFormatConfig } from '../utils/dateTimeFormat';

const Demo = () => {
  // DEBUG: Version Check - Diese Meldung zeigt an ob der neue Build deployed wurde
  console.log('🚀 [Demo.js] VERSION: 2026-01-20-v6 - Trailing Slash Fix!');
  
  const { t } = useTranslation();
  const { showError, showSuccess } = useSnackbar();
  
  const [demoPatients, setDemoPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const init = async () => {
      // Warte auf die Format-Konfiguration, bevor die Patienten geladen werden
      await waitForFormatConfig();
      loadDemoPatients();
    };
    init();
  }, []);

  const loadDemoPatients = async () => {
    try {
      setLoading(true);
      // WICHTIG: Trailing Slash hinzufügen um Server-Redirect zu vermeiden
      // Der Server redirected /demo-patients -> /demo-patients/ mit http:// statt https://
      const apiUrl = createApiUrl('demo-patients/');
      console.log('🔍 [Demo.js] FETCH URL:', apiUrl);
      console.log('🔍 [Demo.js] URL protocol:', new URL(apiUrl).protocol);
      const response = await fetch(apiUrl, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setDemoPatients(data);
      } else {
        showError(t('demo.messages.loadError'));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Demopatienten:', error);
      showError(t('demo.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditPatient(null);
  };

  const handleDialogSuccess = () => {
    loadDemoPatients();
  };

  const handlePatientDoubleClick = (patient) => {
    setEditPatient(patient);
    setDialogOpen(true);
  };

  const handleAddNewPatient = () => {
    setEditPatient(null);
    setDialogOpen(true);
  };

  const handleSelectPatient = (patientId) => {
    setSelectedPatients(prev => {
      if (prev.includes(patientId)) {
        return prev.filter(id => id !== patientId);
      } else {
        return [...prev, patientId];
      }
    });
  };

  const handleSendPatients = async () => {
    if (selectedPatients.length === 0) {
      showError(t('demo.messages.noPatientSelected'));
      return;
    }

    // Check if all selected patients have study_instance_uid
    const patientsToSend = demoPatients.filter(p => selectedPatients.includes(p.id));
    const missingUid = patientsToSend.find(p => !p.study_instance_uid);
    if (missingUid) {
      showError(t('demo.messages.studyInstanceUidMissing', { name: missingUid.patient_name }));
      return;
    }

    setSending(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const patientId of selectedPatients) {
        try {
          const response = await fetch(createApiUrl(`demo-patients/${patientId}/send`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            }
          });

          const data = await response.json();
          if (response.ok && data.success) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to send patient ${patientId}:`, data.message);
          }
        } catch (error) {
          errorCount++;
          console.error(`Error sending patient ${patientId}:`, error);
        }
      }

      if (successCount > 0) {
        showSuccess(t('demo.messages.sendSuccess') + ` (${successCount}/${selectedPatients.length})`);
        setSelectedPatients([]);
        loadDemoPatients(); // Reload the list
      }

      if (errorCount > 0) {
        showError(t('demo.messages.sendError') + ` (${errorCount}/${selectedPatients.length})`);
      }
    } finally {
      setSending(false);
    }
  };

  const handleDeleteStudies = async () => {
    if (selectedPatients.length === 0) {
      showError(t('demo.messages.noPatientSelected'));
      return;
    }

    setDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const patientId of selectedPatients) {
        try {
          const response = await fetch(createApiUrl(`demo-patients/${patientId}/delete-studies`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            }
          });

          const data = await response.json();
          if (response.ok && data.success) {
            successCount++;
            console.log(`Deleted studies for patient ${patientId}:`, data.deleted_counts);
          } else {
            errorCount++;
            console.error(`Failed to delete studies for patient ${patientId}:`, data.message);
          }
        } catch (error) {
          errorCount++;
          console.error(`Error deleting studies for patient ${patientId}:`, error);
        }
      }

      if (successCount > 0) {
        showSuccess(t('demo.messages.deleteStudiesSuccess') + ` (${successCount}/${selectedPatients.length})`);
        setSelectedPatients([]);
      }

      if (errorCount > 0) {
        showError(t('demo.messages.deleteStudiesError') + ` (${errorCount}/${selectedPatients.length})`);
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DemoIcon color="primary" />
          {t('navigation.demo', 'Demo')}
        </Typography>
        
        <Button
          variant="contained"
          className="button"
          startIcon={<PersonAddIcon />}
          onClick={handleAddNewPatient}
        >
          {t('demo.addPatientButton')}
        </Button>
      </Box>
      
      <Paper sx={{ 
        width: '100%', 
        mt: 3, 
        height: 'calc(100vh - 200px)',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ p: 3, flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : demoPatients.length === 0 ? (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 10 }}>
              <Typography variant="h6">
                {t('demo.noPatients')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('demo.noPatientsDescription')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {demoPatients.map((patient) => (
                <Box key={patient.id} sx={{ width: 'calc(25% - 12px)', minWidth: '280px', flexGrow: 0, flexShrink: 0 }}>
                  <Card 
                    elevation={2}
                    onClick={() => handleSelectPatient(patient.id)}
                    onDoubleClick={() => handlePatientDoubleClick(patient)}
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      border: selectedPatients.includes(patient.id) ? '3px solid var(--rd-primary)' : '1px solid rgba(0, 0, 0, 0.12)',
                      backgroundColor: selectedPatients.includes(patient.id) ? 'color-mix(in srgb, var(--rd-primary) 5%, transparent)' : 'background.paper',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                        borderColor: selectedPatients.includes(patient.id) ? 'var(--rd-primary)' : 'primary.main'
                      }
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Header: Patient Name and Metadata */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        {/* Patient Name - Left */}
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'var(--rd-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            mr: 2
                          }}
                          title={patient.patient_name}
                        >
                          {patient.patient_name}
                        </Typography>
                        
                        {/* Accession Number and Study Date - Right */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 'fit-content' }}>
                          {patient.accession_number && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 'medium',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {t('demo.cardLabels.accessionNumber')}: {patient.accession_number}
                            </Typography>
                          )}
                          {patient.observation_date && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 'medium',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {formatDateSync(patient.observation_date.split(' ')[0])}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Patient ID */}
                      <Box sx={{ mb: 2 }}>
                        <Chip 
                          label={`${t('demo.cardLabels.patientId')}: ${patient.patient_id}`}
                          size="small"
                          sx={{ 
                            backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
                            color: 'var(--rd-primary)',
                            fontWeight: 'medium'
                          }}
                        />
                      </Box>
                      
                      {/* Modality */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>{t('demo.cardLabels.modality')}</strong>
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mb: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={patient.modality}
                      >
                        {patient.modality}
                      </Typography>
                      
                      {/* Study Description */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>{t('demo.cardLabels.studyDescription')}</strong>
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mb: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                        title={patient.study_description}
                      >
                        {patient.study_description}
                      </Typography>
                      
                      {/* Question */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>{t('demo.cardLabels.question')}</strong>
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}
                        title={patient.question}
                      >
                        {patient.question || t('demo.cardLabels.noQuestion')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        
        {/* Action Buttons - Bottom Left */}
        {demoPatients.length > 0 && (
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2
          }}>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleSendPatients}
              disabled={sending || selectedPatients.length === 0}
              sx={{
                backgroundColor: 'var(--rd-success)',
                '&:hover': { backgroundColor: 'var(--rd-cal-vacation)' },
                '&:disabled': { backgroundColor: 'grey.400' }
              }}
            >
              {t('demo.sendPatientButton')} {selectedPatients.length > 0 && `(${selectedPatients.length})`}
            </Button>
            
            <Button
              variant="contained"
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              onClick={handleDeleteStudies}
              disabled={deleting || selectedPatients.length === 0}
              sx={{
                backgroundColor: 'var(--rd-error)',
                '&:hover': { backgroundColor: 'var(--rd-danger)' },
                '&:disabled': { backgroundColor: 'grey.400' }
              }}
            >
              {t('demo.deleteStudiesButton')} {selectedPatients.length > 0 && `(${selectedPatients.length})`}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Add/Edit Demo Patient Dialog */}
      <AddDemoPatientDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editPatient={editPatient}
      />
    </Box>
  );
};

export default Demo;